import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { apiFetch } from '../../api/client';
import { HUD_CTA_BUTTON, HUD_DIALOG_CONTENT, HUD_DIALOG_PAPER, HUD_DIALOG_TITLE } from './hudDialogStyles';

/**
 * Seleção de usuários que autorizam e assinam o TR (Atividades + Assinatura Digital).
 */
export default function AutorizadoresTrDialog({
  open,
  onClose,
  solicitacao,
  usuarios = [],
  loadingUsuarios = false,
  onConfirm,
}) {
  const [ids, setIds] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!open || !solicitacao?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await apiFetch(`/api/contratos/solicitacoes/${solicitacao.id}/autorizadores`);
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && Array.isArray(data.autorizadores)) {
          setIds(data.autorizadores.map((a) => a.id).filter((id) => id != null));
        }
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, solicitacao?.id]);

  const handleConfirm = async () => {
    if (!ids.length) {
      setError('Selecione ao menos um autorizador/assinante.');
      return;
    }
    const autorizadores = ids
      .map((id) => {
        const u = usuarios.find((x) => String(x.id) === String(id));
        if (!u) return null;
        return { id: u.id, nome: u.nome, email: u.email };
      })
      .filter((a) => a?.email);

    if (!autorizadores.length) {
      setError('Os usuários selecionados precisam ter e-mail cadastrado.');
      return;
    }

    setEnviando(true);
    setError('');
    try {
      await onConfirm?.(autorizadores, solicitacao);
    } catch (err) {
      setError(err.message || 'Erro ao enviar');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={open} onClose={enviando ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: HUD_DIALOG_PAPER }}>
      <DialogTitle sx={HUD_DIALOG_TITLE}>
        Autorizar e assinar TR
      </DialogTitle>
      <DialogContent sx={HUD_DIALOG_CONTENT} dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Marque os usuários que devem <strong>autorizar</strong> (Atividades) e{' '}
          <strong>assinar digitalmente</strong> o Termo de Referência. Em seguida o processo de
          contratação será aberto.
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Solicitação: <strong>{solicitacao?.codigo || solicitacao?.id}</strong>
          {solicitacao?.titulo ? ` — ${solicitacao.titulo}` : ''}
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <FormControl fullWidth size="small" error={Boolean(error)}>
            <InputLabel>Autorizadores / assinantes</InputLabel>
            <Select
              multiple
              label="Autorizadores / assinantes"
              value={ids}
              onChange={(e) => {
                setIds(e.target.value);
                setError('');
              }}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected || []).map((id) => {
                    const u = usuarios.find((x) => String(x.id) === String(id));
                    return <Chip key={id} size="small" label={u?.nome || id} />;
                  })}
                </Box>
              )}
            >
              {usuarios.map((usuario) => (
                <MenuItem key={usuario.id} value={usuario.id}>
                  <Checkbox checked={ids.some((id) => String(id) === String(usuario.id))} />
                  <ListItemText primary={usuario.nome} secondary={usuario.email || undefined} />
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {error ||
                (loadingUsuarios
                  ? 'Carregando usuários...'
                  : 'Cada selecionado recebe atividade + participa da Assinatura Digital do TR.')}
            </FormHelperText>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={enviando} sx={{ textTransform: 'none' }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          startIcon={enviando ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          disabled={enviando || loading}
          onClick={handleConfirm}
          sx={{ ...HUD_CTA_BUTTON, textTransform: 'none' }}
        >
          Enviar para Análise
        </Button>
      </DialogActions>
    </Dialog>
  );
}
